// Multi-entity G8: posting an approved contractor run to the PAYING company's
// ledger. Dry run first: the preview shows every line (fee, withholding,
// payable on the payee's vendor) and every blocker. A payee without a vendor
// in that ledger is bound right here; an unmapped account is fixed in the
// accounting mapping screen. Post is a separate, confirmed press, and posts
// once (a second press reports "already posted").
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import { useHasPermission } from '@/rbac/useRbac';
import {
  bindPayeeVendor, listLedgerVendors, postContractorPayment, postContractorRun, previewContractorPayment,
  previewContractorPosting, type ContractorDraft, type ContractorPaymentDraft, type LedgerVendor,
} from '@/services/accountingService';

const STATE: Record<string, string> = {
  posted: 'posted', not_posted: 'not posted', not_required: '—', failed: 'failed', unknown: 'check the ledger',
};

const P = 'quickbooks' as const;

export const ContractorAccountingPanel: React.FC<{ runId: string; legalEntityId?: string | null }> = ({ runId, legalEntityId }) => {
  const canSee = useHasPermission('accounting:read');
  const canPost = useHasPermission('accounting:post');
  const canMap = useHasPermission('accounting:map');
  const confirm = useConfirm();
  const [draft, setDraft] = useState<ContractorDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vendors, setVendors] = useState<LedgerVendor[] | null>(null);
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [payment, setPayment] = useState<ContractorPaymentDraft | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const loadPayment = async () => {
    setPaymentError(null);
    try {
      setPayment(await previewContractorPayment(P, runId));
    } catch (e) {
      setPayment(null);
      setPaymentError(getApiErrorMessage(e, 'Could not preview the payment leg.'));
    }
  };

  const postPayments = async () => {
    if (!payment) return;
    const n = payment.lines.filter((l) => l.posting === 'will_post').length;
    const ok = await confirm({
      title: 'Post contractor payments',
      message: `Post ${n} payment${n === 1 ? '' : 's'} from ${payment.bank_account_name || 'the mapped bank account'} to ${payment.company_name || "the paying company's ledger"}? Payouts that failed or are not paid yet are left out.`,
      confirmLabel: 'Post payments',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await postContractorPayment(P, runId);
      toast.success('Payments posted to accounting.');
      await loadPayment();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not post the payments.'), { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  const load = useCallback(async () => {
    setError(null);
    try {
      setDraft(await previewContractorPosting(P, runId));
    } catch (e) {
      setDraft(null);
      setError(getApiErrorMessage(e, 'Could not preview this run for accounting.'));
    }
  }, [runId]);

  useEffect(() => {
    if (canSee) void load();
  }, [canSee, load]);

  const unbound = (draft?.issues ?? []).filter((i) => i.code === 'VENDOR_BINDING_MISSING');
  useEffect(() => {
    if (unbound.length && canMap && legalEntityId && vendors === null) {
      listLedgerVendors(P, legalEntityId).then(setVendors).catch(() => setVendors([]));
    }
  }, [unbound.length, canMap, legalEntityId, vendors]);

  if (!canSee) return null;

  const bind = async (payeeId: string, payeeName: string) => {
    const v = vendors?.find((x) => x.id === choice[payeeId]);
    if (!v || !legalEntityId) return;
    setBusy(true);
    try {
      await bindPayeeVendor(P, legalEntityId, { payee_id: payeeId, payee_name: payeeName,
        external_vendor_id: v.id, external_vendor_name: v.display_name });
      toast.success(`${payeeName} bound to ${v.display_name}.`);
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not bind the vendor.'));
    } finally {
      setBusy(false);
    }
  };

  const post = async () => {
    if (!draft) return;
    const ok = await confirm({
      title: 'Post to accounting',
      message: `Post this run to ${draft.company_name || "the paying company's ledger"} on ${draft.doc_date} (${draft.currency} ${draft.total_dr})?`,
      confirmLabel: 'Post',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const out = await postContractorRun(P, runId);
      toast.success(out.already_posted ? 'Already posted — nothing was sent again.' : 'Posted to accounting.');
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not post this run.'), { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-border p-3 space-y-2 text-sm" data-testid="contractor-accounting">
      <div className="font-medium text-foreground">Accounting</div>
      {error && <p className="text-danger-strong" role="alert">{error}</p>}
      {!error && !draft && <p className="text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Preparing preview…</p>}
      {draft && (
        <>
          <p className="text-muted-foreground">
            {draft.company_name ? `${draft.company_name} · ` : ''}{draft.doc_date} · {draft.currency}{' '}
            {draft.ready ? <Badge variant="success">ready</Badge> : <Badge variant="warning">not ready</Badge>}
          </p>
          {draft.lines.map((l, i) => (
            <div key={i} className="flex justify-between gap-2 tabular-nums">
              <span className="text-muted-foreground">
                {l.account_name || l.logical_code}{l.vendor_name ? ` · ${l.vendor_name}` : ''} — {l.description}
              </span>
              <span>{l.dr_cr} {l.amount}</span>
            </div>
          ))}
          {draft.issues.filter((i) => i.code !== 'VENDOR_BINDING_MISSING').map((i, k) => (
            <p key={k} className={i.blocking ? 'text-danger-strong' : 'text-muted-foreground'}>{i.message}</p>
          ))}
          {unbound.map((i) => (
            <div key={i.source_id} className="flex flex-wrap items-center gap-2" data-testid="vendor-binding">
              <span className="text-danger-strong">{i.message}</span>
              {canMap && vendors && (
                <>
                  <select aria-label={`Vendor for ${i.source_label}`} value={choice[i.source_id ?? ''] ?? ''}
                    onChange={(e) => setChoice((c) => ({ ...c, [i.source_id ?? '']: e.target.value }))}
                    className="h-8 rounded-md border border-border bg-card px-2 text-sm text-foreground">
                    <option value="">Choose a vendor…</option>
                    {vendors.filter((v) => v.active).map((v) => <option key={v.id} value={v.id}>{v.display_name}</option>)}
                  </select>
                  <Button size="sm" variant="outline" disabled={busy || !choice[i.source_id ?? '']}
                    onClick={() => void bind(i.source_id ?? '', i.source_label ?? '')}>Bind</Button>
                </>
              )}
            </div>
          ))}
          {draft.mode === 'bill' && (draft.bills ?? []).length > 0 && (
            <div className="space-y-1" data-testid="contractor-bills">
              {(draft.bills ?? []).map((b) => (
                <div key={b.line_id} className="flex flex-wrap justify-between gap-2">
                  <span className="text-muted-foreground">
                    Bill · {b.payee_name}{b.vendor_name ? ` (${b.vendor_name})` : ''}: {b.gross}
                    {b.credit_status !== 'not_required' ? ` · WHT credit ${b.wht}` : ''}
                  </span>
                  <span>
                    bill {STATE[b.bill_status] ?? b.bill_status}
                    {b.credit_status !== 'not_required' ? ` · credit ${STATE[b.credit_status] ?? b.credit_status}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
          {canPost && (
            <Button size="sm" disabled={busy || !draft.ready} onClick={() => void post()}>
              {draft.mode === 'bill' ? 'Post bills' : 'Post to accounting'}
            </Button>
          )}
          <div className="pt-2 space-y-1" data-testid="contractor-payment">
            <Button size="sm" variant="ghost" onClick={() => void loadPayment()}>Payment leg</Button>
            {paymentError && <p className="text-danger-strong" role="alert">{paymentError}</p>}
            {payment && (
              <>
                {payment.lines.map((l) => (
                  <div key={l.line_id} className="flex flex-wrap justify-between gap-2">
                    <span className="text-muted-foreground">{l.payee_name}: {l.net}{l.paid_on ? ` · paid ${l.paid_on}` : ''}</span>
                    <span>{l.posting === 'will_post' ? 'will post' : l.posting === 'posted' ? 'posted' : l.reason || 'not eligible'}</span>
                  </div>
                ))}
                {payment.issues.map((i, k) => (
                  <p key={k} className={i.blocking ? 'text-danger-strong' : 'text-muted-foreground'}>{i.message}</p>
                ))}
                {canPost && (
                  <Button size="sm" disabled={busy || !payment.ready} onClick={() => void postPayments()}>Post payments</Button>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};
